import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import { normalizeLocale } from '../utils/localeDetection';

// Hardcoded catalogue of every UI language the app ships. This is the SINGLE
// SOURCE OF TRUTH that drives the LanguageSwitcher (native names + flags) and
// the loader below. Drop a locales/<code>.json file in and add a row here to
// light up a new language — no other list edit is required.
export const LOCALES = [
  { code: 'en', englishName: 'English', nativeName: 'English', flag: 'US', dir: 'ltr' },
  { code: 'zh', englishName: 'Chinese', nativeName: '中文', flag: 'CN', dir: 'ltr' },
  { code: 'es', englishName: 'Spanish', nativeName: 'Español', flag: 'ES', dir: 'ltr' },
  { code: 'fr', englishName: 'French', nativeName: 'Français', flag: 'FR', dir: 'ltr' },
  { code: 'it', englishName: 'Italian', nativeName: 'Italiano', flag: 'IT', dir: 'ltr' },
  { code: 'ru', englishName: 'Russian', nativeName: 'Русский', flag: 'RU', dir: 'ltr' },
  { code: 'pt', englishName: 'Portuguese', nativeName: 'Português', flag: 'PT', dir: 'ltr' },
  { code: 'ar', englishName: 'Arabic', nativeName: 'العربية', flag: 'SA', dir: 'rtl' },
  { code: 'hi', englishName: 'Hindi', nativeName: 'हिन्दी', flag: 'IN', dir: 'ltr' },
  { code: 'ms', englishName: 'Malay', nativeName: 'Bahasa Melayu', flag: 'MY', dir: 'ltr' },
  { code: 'id', englishName: 'Indonesian', nativeName: 'Bahasa Indonesia', flag: 'ID', dir: 'ltr' },
  { code: 'ja', englishName: 'Japanese', nativeName: '日本語', flag: 'JP', dir: 'ltr' },
  { code: 'ko', englishName: 'Korean', nativeName: '한국어', flag: 'KR', dir: 'ltr' },
  { code: 'bn', englishName: 'Bengali', nativeName: 'বাংলা', flag: 'BD', dir: 'ltr' },
  { code: 'ur', englishName: 'Urdu', nativeName: 'اردو', flag: 'PK', dir: 'rtl' },
  { code: 'ta', englishName: 'Tamil', nativeName: 'தமிழ்', flag: 'IN', dir: 'ltr' },
  { code: 'vi', englishName: 'Vietnamese', nativeName: 'Tiếng Việt', flag: 'VN', dir: 'ltr' },
  { code: 'my', englishName: 'Burmese', nativeName: 'မြန်မာ', flag: 'MM', dir: 'ltr' },
  { code: 'th', englishName: 'Thai', nativeName: 'ไทย', flag: 'TH', dir: 'ltr' },
  { code: 'el', englishName: 'Greek', nativeName: 'Ελληνικά', flag: 'GR', dir: 'ltr' },
  { code: 'tr', englishName: 'Turkish', nativeName: 'Türkçe', flag: 'TR', dir: 'ltr' },
  { code: 'fa', englishName: 'Persian', nativeName: 'فارسی', flag: 'IR', dir: 'rtl' },
];

// Lazy-load locale: load only the active language on demand instead of bundling all.
// Each locale is a separate chunk; only the detected/selected language is fetched.
//
// A locale's MAIN translation (locales/<lng>.json) must exist. Sub-resources
// (items / languageLearning / studioMusicians / studioPlayers) fall back to `en`
// for every locale EXCEPT the four "complete" ones below — that is what lets a
// newly added language light up without every sub-file being authored first.
const COMPLETE_LOCALES = ['en', 'zh', 'es', 'fr'];
const subLocaleFor = (lng) => (COMPLETE_LOCALES.includes(lng) ? lng : 'en');

// openbadge.me UI copy. Authored in English + Simplified Chinese; every other
// shipped locale cleanly falls back to the English openbadge file (handled by the
// rejected dynamic import below + i18next's key-level fallbackLng 'en').
const OPENBADGE_LOCALES = ['en', 'zh'];
const openbadgeLocaleFor = (lng) => (OPENBADGE_LOCALES.includes(lng) ? lng : 'en');

// The School platform ships its own sub-resource. Unlike `items` (authored in
// all four complete locales) the School platform is only translated into
// English and Chinese, so every other locale deliberately falls back to the
// English School file rather than 404ing into raw translation keys.
const schoolLocaleFor = (lng) => (lng === 'zh' ? 'zh' : 'en');

// Discover the root <lng>.json files that actually exist on disk so a locale
// listed in LOCALES without a file simply falls back to `en` instead of 404ing.
// Uses webpack's require.context (build-time). Under jest/Node where that API
// is unavailable, fall back to the full LOCALES list so tests keep passing.
// Every catalogue entry ships a locales/<code>.json, so LOCALES doubles as the
// availability list. Deliberately NOT require.context: that API is eager and
// inlined all 22 locale JSONs (~1.8MB) into the main bundle, so a visitor
// downloaded every language to read one. loadLocale's dynamic import() below
// already emits each locale as its own chunk, fetched only when active.
const AVAILABLE_LOCALES = LOCALES.map((l) => l.code);

const mergeTranslations = (main, items, languageLearning, studioMusicians, studioPlayers, school, openbadge) => ({
  ...main,
  items: items?.items ?? {},
  ...(languageLearning || {}),
  ...(studioMusicians || {}),
  ...(studioPlayers || {}),
  ...(school || {}),
  ...(openbadge || {}),
});

const loadLocale = (lng) => {
  const resolved = AVAILABLE_LOCALES.includes(lng) ? lng : 'en';
  const sub = subLocaleFor(resolved);
  return Promise.allSettled([
    import(`./locales/${resolved}.json`),
    import(`./locales/items/${sub}.json`),
    import(`./locales/languageLearning/${sub}.json`),
    import(`./locales/studioMusicians/${sub}.json`),
    import(`./locales/studioPlayers/${sub}.json`),
    import(`./locales/school/${schoolLocaleFor(resolved)}.json`),
    import(`./locales/openbadge/${openbadgeLocaleFor(resolved)}.json`),
  ]).then(
    (results) => {
      const failures = results.filter(r => r.status === 'rejected');
      if (failures.length) {
        console.warn(`[i18n] ${failures.length} translation file(s) failed to load:`, failures.map(r => r.reason?.message).filter(Boolean));
      }
      const [main, items, languageLearning, studioMusicians, studioPlayers, school, openbadge] = results.map(
        r => r.status === 'fulfilled' ? r.value : { default: {} }
      );
      return mergeTranslations(
        main.default ?? main,
        items.default ?? items,
        languageLearning.default ?? languageLearning,
        studioMusicians.default ?? studioMusicians,
        studioPlayers.default ?? studioPlayers,
        school.default ?? school,
        openbadge.default ?? openbadge,
      );
    },
  );
};

i18n
  .use(LanguageDetector)
  .use(
    resourcesToBackend((language) => loadLocale(normalizeLocale(language)))
  )
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    ns: ['translation'],
    defaultNS: 'translation',
    debug: false,

    // Restrict to the locales we actually ship so an unsupported browser
    // language cleanly falls back to `en` (still generic — a logged-in user's
    // stored choice is applied via applyUserLocale regardless).
    supportedLngs: AVAILABLE_LOCALES,
    nonExplicitSupportedLngs: true,

    react: {
      useSuspense: true, // Suspend until active locale is loaded
    },

    interpolation: {
      escapeValue: false,
    },

    detection: {
      // localStorage = explicit user choice (incl. logged-in profile locale
      // mirrored by the backend's i18nextLng cookie); navigator/htmlTag =
      // the source request's Accept-Language for first-time anonymous visitors;
      // cookie = backend-set default (see integrations/localeDetectionMiddleware).
      order: ['localStorage', 'navigator', 'htmlTag', 'cookie'],
      lookupLocalStorage: 'i18nextLng',
      lookupCookie: 'i18nextLng',
      caches: ['localStorage', 'cookie'],
    },
  });

// Keep <html lang> and text direction (RTL for ar/ur/fa) in sync with the
// active language so localized layouts render correctly.
function applyDocDir(lng) {
  if (typeof document === 'undefined') return;
  const base = String(lng || '').split('-')[0];
  const loc = LOCALES.find((l) => l.code === base);
  document.documentElement.lang = lng;
  document.documentElement.dir = loc?.dir === 'rtl' ? 'rtl' : 'ltr';
}
i18n.on('languageChanged', applyDocDir);
applyDocDir(i18n.language || 'en');

// Apply a logged-in user's stored locale preference (overrides request detection).
// Call this from the auth flow once the user profile is loaded.
export const applyUserLocale = (locale) => {
  const lng = normalizeLocale(locale);
  if (AVAILABLE_LOCALES.includes(lng)) {
    i18n.changeLanguage(lng);
  }
};

export default i18n;
